import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AxialupLogo from './AxialupLogo';
import { 
  House, 
  Users, 
  Buildings, 
  CurrencyInr, 
  ShoppingCart, 
  ArrowCounterClockwise,
  ChartBar,
  SignOut,
  List,
  X,
  Flame,
  Wallet,
  CalendarCheck,
  HandCoins,
  Siren,
  CaretUp,
  CaretDown,
  Lightning,
  ClipboardText,
  AmazonLogo,
  Receipt,
  FileText,
  PaperPlaneTilt,
  UploadSimple,
  Headset,
  UsersFour,
  DeviceMobile,
  MagnifyingGlass,
  Robot,
  Bank,
  Invoice,
  TrendUp,
  Vault,
  Archive,
  FloppyDisk,
  Gift,
  CreditCard,
  Shield,
  Gear,
  Tray,
  FileXls,
  TestTube,
  TelegramLogo,
  ChatText,
  Trash
} from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';

const API_BASE = window.location.origin;

export default function Sidebar() {
  const { user, logout, isOwner, isAssistant, isAdminOrAbove, isDistributor, isSalesPerson } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => {
    // Persist user's collapse preferences across sessions.
    try { return JSON.parse(localStorage.getItem('sidebarOpenSections') || '{}'); }
    catch (e) { return {}; }
  });
  const [distBalance, setDistBalance] = useState(null);

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sidebarOpenSections', JSON.stringify(next)); } catch (e) { /* empty */ }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isStaff = user?.role === 'staff' || user?.role === 'sub_staff';
  const isTeamLead = user?.role === 'staff';
  const isSubStaff = user?.role === 'sub_staff';
  const isVendor = ['gift_card_vendor', 'recharge_vendor', 'electricity_vendor', 'card_vendor'].includes(user?.role);
  const isElectricityVendor = user?.role === 'electricity_vendor';
  const isCardVendor = user?.role === 'card_vendor';

  // Fetch distributor balance for sidebar display
  const fetchDistBalance = useCallback(async () => {
    if (!isDistributor) return;
    try {
      const res = await fetch(`${API_BASE}/api/distributor/dashboard`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDistBalance(data.total_balance);
      }
    } catch (err) { console.error('Dist balance fetch error:', err); }
  }, [isDistributor]);

  useEffect(() => {
    fetchDistBalance();
    if (isDistributor) {
      const interval = setInterval(fetchDistBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchDistBalance, isDistributor]);

  const formatCurrency = (val) => {
    if (val == null) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);
  };

  // ─── Complaints Hub access check ───
  const [hubAccess, setHubAccess] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setHubAccess(false); return; }
    fetch(`${API_BASE}/api/complaints-hub/my-access`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setHubAccess(!!d?.has_access); })
      .catch(() => { if (!cancelled) setHubAccess(false); });
    return () => { cancelled = true; };
  }, [user]);

  const isAccountant = user?.role === 'accountant';
  const userOilCompanies = (user?.oil_companies || []).map(o => String(o).toUpperCase());
  const distributorHasHpcl = isDistributor && userOilCompanies.includes('HPCL');
  const canAccessHpclMatcher = isAdminOrAbove || isAccountant || isStaff || distributorHasHpcl;
  // Bharat Gas / BPCL Tool — ONLY Admin/Owner, Assistant, and Bharat/BPCL distributor. CA & Staff RESTRICTED.
  const distributorHasBharat = isDistributor && userOilCompanies.some(o => o.includes('BHARAT') || o.includes('BPCL'));
  const canAccessBharatGasTool = isAdminOrAbove || isAssistant || distributorHasBharat;

  // ─── Standalone items (no group) ───
  const topItems = [
    { to: '/', icon: House, label: 'Dashboard', show: !isDistributor && !isSalesPerson && !isVendor },
    { to: '/distributor-dashboard', icon: House, label: 'Dashboard', show: isDistributor },
    { to: '/sales-persons', icon: UsersFour, label: 'My Commission', show: isSalesPerson },
    // Distributor-only quick actions
    { to: '/distributor-submit', icon: UploadSimple, label: 'Submit Booking', show: isDistributor },
    { to: '/distributor-documents', icon: FileText, label: 'Documents', show: isDistributor },
    { to: '/distributor-ledger', icon: Buildings, label: 'Ledger', show: isDistributor },
    // Sales person
    { to: '/bookings', icon: ShoppingCart, label: 'Booking History', show: isSalesPerson },
  ];

  // ─── Vendor-only items (separate flat menu for vendor roles) ───
  const vendorItems = [
    { to: '/gift-card-vendors', icon: Gift, label: 'My Dashboard', show: isVendor && !isCardVendor && !isElectricityVendor },
    { to: '/vendor/my-cards', icon: CreditCard, label: 'My Cards', show: isCardVendor },
    { to: '/electricity-vendor/upload', icon: Lightning, label: 'My Bills', show: isElectricityVendor },
    { to: '/electricity-ledger', icon: Wallet, label: 'My Ledger', show: isElectricityVendor },
    { to: '/vendor/wb-request', icon: Wallet, label: 'WB Request', show: isVendor && !isElectricityVendor },
    { to: '/vendor/all-gift-cards', icon: Gift, label: 'Complaints & Reversals', show: isVendor && !isCardVendor && !isElectricityVendor },
    { to: '/vendor/transactions', icon: Receipt, label: 'Transaction History', show: isVendor && !isCardVendor && !isElectricityVendor },
    { to: '/vendor/ledger', icon: Wallet, label: 'Ledger', show: isVendor && !isElectricityVendor },
  ];

  // ─── Grouped sections (collapsible folders) ───
  const sections = [
    {
      key: 'bookings', label: 'Bookings', icon: ShoppingCart,
      items: [
        { to: '/bookings', icon: ShoppingCart, label: 'Booking History', show: (isAdminOrAbove || isAccountant) },
        { to: '/booking-history', icon: MagnifyingGlass, label: 'Booking History & Audit', show: isAdminOrAbove || isAccountant || isStaff || isSubStaff },
        { to: '/bulk-booking', icon: Lightning, label: 'Bulk Entry', show: !isDistributor && !isStaff && !isSalesPerson && !isAccountant && !isVendor },
        { to: '/quick-booking', icon: Lightning, label: 'Quick Booking', show: isAdminOrAbove },
        { to: '/booking-files', icon: FileText, label: 'Booking Files', show: isAdminOrAbove || isAccountant },
        { to: '/my-booking-workspace', icon: FileText, label: 'My Booking Workspace', show: isSubStaff },
        { to: '/pending-reviews', icon: Tray, label: 'Pending Reviews', show: isTeamLead || isAdminOrAbove },
        { to: '/my-team-report', icon: FileText, label: 'My Team Bookings', show: isTeamLead },
        { to: '/assigned-files', icon: FileText, label: 'Assigned Files', show: isTeamLead },
        { to: '/auto-assign', icon: FileText, label: 'Auto-Assign', show: isOwner },
        { to: '/phase2-preview', icon: FileText, label: 'Phase 2 Preview', show: isOwner },
        { to: '/balance-transfer', icon: FileText, label: 'Balance Transfer', show: isTeamLead || isSubStaff || isAdminOrAbove },
      ],
    },
    {
      key: 'staff', label: 'Staff Management', icon: Users,
      items: [
        { to: '/staff', icon: Users, label: 'Staff', show: isAdminOrAbove || isAccountant },
        { to: '/distributors', icon: Buildings, label: 'Distributors', show: isOwner },
        { to: '/sales-persons', icon: UsersFour, label: 'Sales Persons', show: isAdminOrAbove || isAccountant },
        { to: '/accountants', icon: Vault, label: 'Accountants (CA)', show: isOwner },
        { to: '/my-team', icon: Users, label: 'My Team', show: user?.role === 'staff' },
        { to: '/account-requests', icon: HandCoins, label: 'Account Requests', show: isOwner || (user?.role === 'admin') || isAssistant },
        { to: '/my-account-requests', icon: HandCoins, label: 'My Account Requests', show: isStaff || isSubStaff },
      ],
    },
    {
      key: 'agencies', label: 'Agencies & Distributors', icon: Buildings,
      items: [
        { to: '/agencies', icon: Buildings, label: 'Agencies', show: isAdminOrAbove || isAccountant },
        { to: '/agency-files', icon: PaperPlaneTilt, label: 'Agency Files', show: isAdminOrAbove },
        { to: '/pending-opening-balances', icon: Buildings, label: 'Pending Opening Balances', show: isAdminOrAbove },
        { to: '/distributor-fund-requests', icon: HandCoins, label: 'Dist. Fund Requests', show: isAdminOrAbove || isAccountant },
        { to: '/payment-recovery', icon: Siren, label: 'Payment Recovery', show: isAdminOrAbove || isAccountant },
      ],
    },
    {
      key: 'finance', label: 'Finance', icon: Vault,
      items: [
        { to: '/balance', icon: Wallet, label: 'Balance', show: isOwner || isAccountant },
        { to: '/daily-balance', icon: CalendarCheck, label: 'Daily Balance', show: isOwner || isAccountant },
        { to: '/cashback', icon: TrendUp, label: 'Cashback Tracker', show: isOwner || isAccountant },
        { to: '/deposits', icon: CurrencyInr, label: 'Deposits', show: isAdminOrAbove || isAccountant },
        { to: '/fund-requests', icon: HandCoins, label: 'Card/Gift Card Fund Request', show: isAdminOrAbove || isAccountant || isStaff },
        { to: '/refunds', icon: ArrowCounterClockwise, label: 'Refunds', show: isAdminOrAbove || isAccountant },
        { to: '/amazon-refunds', icon: AmazonLogo, label: 'Amazon Refunds', show: isAdminOrAbove || isAccountant },
        { to: '/settlements', icon: Bank, label: 'Cash Settlements', show: isAdminOrAbove },
        { to: '/invoices', icon: Invoice, label: 'Invoices', show: isAdminOrAbove || isDistributor },
        { to: '/bank-statements', icon: Bank, label: 'Bank Statements', show: isAdminOrAbove || isAccountant || isAssistant },
        { to: '/reports', icon: ChartBar, label: 'Reports', show: isAdminOrAbove || isAccountant },
      ],
    },
    {
      key: 'stock', label: 'Stock & Cards', icon: CreditCard,
      items: [
        { to: '/account-stock', icon: AmazonLogo, label: 'Account Stock (Amazon/TataNeu)', show: isOwner || (user?.role === 'admin') },
        { to: '/used-amazon-accounts', icon: AmazonLogo, label: '🚫 Used Amazon Accounts', show: isOwner || (user?.role === 'admin') },
        { to: '/card-stock', icon: CreditCard, label: 'Card Stock', show: isOwner },
        { to: '/voucher-stock', icon: Gift, label: 'Voucher Stock', show: isOwner },
        { to: '/platform-accounts', icon: DeviceMobile, label: 'Platform Accounts', show: isAdminOrAbove || isStaff || isAccountant },
        { to: '/my-cards', icon: CreditCard, label: 'My Cards (Team)', show: isTeamLead },
        { to: '/my-card-hub', icon: CreditCard, label: '💳 My Card Hub', show: isStaff || (user?.role === 'sub_staff') },
        { to: '/my-vouchers', icon: Gift, label: 'My Voucher Basket', show: isStaff },
      ],
    },
    {
      key: 'vendors-admin', label: 'Vendor Management', icon: Gift,
      items: [
        { to: '/gift-card-vendors', icon: Gift, label: 'Vendors & Card Inventory', show: isAdminOrAbove || isAccountant },
        { to: '/admin/fund-requests', icon: Wallet, label: 'Vendor Fund Requests', show: isAdminOrAbove },
        { to: '/admin/vendor-cards', icon: CreditCard, label: 'Vendor Cards', show: isAdminOrAbove },
        { to: '/deleted-vendors', icon: Trash, label: 'Recently Deleted (Recovery)', show: isAdminOrAbove },
      ],
    },
    {
      key: 'electricity', label: 'Electricity Bookings', icon: Lightning,
      items: [
        { to: '/electricity-files', icon: FileText, label: 'Vendor Files', show: isAdminOrAbove || isAssistant },
        { to: '/electricity-workspace', icon: Lightning, label: 'My Workspace', show: isStaff || isSubStaff || isAdminOrAbove },
      ],
    },
    {
      key: 'ledgers', label: 'Ledgers', icon: Wallet,
      items: [
        { to: '/vendor-ledgers/card', icon: CreditCard, label: 'Card Vendor Ledger', show: isAdminOrAbove || isAccountant || isAssistant },
        { to: '/vendor-ledgers/gift-card', icon: Gift, label: 'Gift Card Vendor Ledger', show: isAdminOrAbove || isAccountant || isAssistant },
        { to: '/vendor-ledgers/recharge', icon: DeviceMobile, label: 'Recharge Vendor Ledger', show: isAdminOrAbove || isAccountant || isAssistant },
        { to: '/vendor-ledgers/electricity', icon: Lightning, label: 'Electricity Vendor Ledger', show: isAdminOrAbove || isAccountant || isAssistant },
        { to: '/distributor-ledger', icon: Buildings, label: 'Distributor Ledger', show: isAdminOrAbove || isAccountant },
        { to: '/wallet-statement', icon: Receipt, label: 'Wallet Statement', show: isAdminOrAbove || isStaff || isAccountant },
      ],
    },
    {
      key: 'tools', label: 'Tools', icon: Robot,
      items: [
        { to: '/hpcl-matcher', icon: MagnifyingGlass, label: 'HPCL Data Matcher', show: canAccessHpclMatcher },
        { to: '/bharat-gas-tool', icon: Flame, label: 'Bharat Gas Finder', show: canAccessBharatGasTool },
        { to: '/indane-extractor', icon: FileXls, label: 'Indane Data Extractor', show: isAdminOrAbove || isAssistant || isStaff },
        { to: '/lpg-checker', icon: MagnifyingGlass, label: 'LPG Bill Checker', show: isOwner },
        { to: '/amazon-balance', icon: AmazonLogo, label: 'Amazon Balance Check', show: isAdminOrAbove || isAssistant || isStaff || isSubStaff },
        { to: '/amazon-balance/accounts', icon: AmazonLogo, label: 'Amazon Balance — Accounts', show: isAdminOrAbove },
        { to: '/amazon-balance/audit', icon: AmazonLogo, label: 'Amazon Balance — Audit', show: isAdminOrAbove },
        { to: '/admin/voucher-extractor', icon: Gift, label: 'Kalyan/Tanishq Voucher Extractor', show: isOwner || user?.role === 'admin' },
        { to: '/automation', icon: Robot, label: 'Automation', show: isOwner },
      ],
    },
    {
      key: 'logs', label: 'Logs & Audit', icon: ClipboardText,
      items: [
        { to: '/audit-log', icon: ClipboardText, label: 'Audit Log', show: isOwner || isAccountant },
        { to: '/failed-bookings-log', icon: CreditCard, label: 'Failed Bookings', show: isAdminOrAbove || isAccountant || isStaff },
        { to: '/card-audit-trail', icon: CreditCard, label: 'Card Audit Trail', show: isAdminOrAbove || isAccountant || isStaff },
        { to: '/voucher-complaints', icon: Siren, label: 'Voucher Complaints', show: isOwner },
        { to: '/duplicate-voucher-log', icon: Archive, label: 'Duplicate Voucher Log', show: isOwner },
        { to: '/admin/recovery-queue', icon: ClipboardText, label: 'Recovery Queue', show: isAdminOrAbove || isAccountant || isStaff },
        { to: '/admin/security-audit', icon: Shield, label: 'Security Audit', show: isOwner },
        { to: '/admin/data-reset', icon: ArrowCounterClockwise, label: '🧹 Data Reset & Opening', show: isOwner || isAccountant || isAssistant },
        { to: '/admin/bulk-khatabook', icon: ArrowCounterClockwise, label: '📥 Bulk KhataBook Import', show: isOwner || isAccountant || isAssistant },
        { to: '/admin/data-recovery', icon: ArrowCounterClockwise, label: '🔙 Data Recovery', show: isOwner || isAccountant || isAssistant },
      ],
    },
    {
      key: 'security', label: 'Security', icon: Shield,
      items: [
        { to: '/security/2fa', icon: Shield, label: '🛡️ Two-Factor Auth', show: !!user },
      ],
    },
    {
      key: 'admin', label: 'Admin', icon: Gear,
      items: [
        { to: '/settings', icon: Gear, label: 'Settings', show: isOwner || isAccountant || isAssistant },
        { to: '/telegram', icon: TelegramLogo, label: 'Telegram Alerts', show: true },
        { to: '/archived', icon: Archive, label: 'Archived Items', show: isOwner || isAssistant || isAccountant },
        { to: '/backup', icon: FloppyDisk, label: 'Data Backup', show: isOwner || isAccountant },
        { to: '/admin/test-runs', icon: TestTube, label: 'Regression Tests', show: isOwner || isAccountant || isAssistant },
      ],
    },
  ];

  // Bottom standalone items (always available)
  const isPayUser = ['staff', 'sub_staff', 'distributor', 'sales_person'].includes(user?.role);
  const bottomItems = [
    { to: '/chat', icon: ChatText, label: '💬 Chat', show: true, highlight: true },
    { to: '/complaints-hub', icon: Siren, label: 'Complaints Hub', show: hubAccess, highlight: true },
    { to: '/complaints', icon: Siren, label: 'Complaints', show: isAdminOrAbove || isAccountant || isStaff || isSubStaff || isDistributor },
    { to: '/support', icon: Headset, label: 'Support', show: true },
    { to: '/bbps', icon: Receipt, label: '🏦 BBPS Bill Pay', show: isPayUser || isAdminOrAbove, highlight: true },
    { to: '/bbps-admin', icon: Bank, label: '🏦 BBPS Admin', show: isAdminOrAbove },
    { to: '/security/2fa-admin', icon: Shield, label: '🛡️ 2FA Admin', show: isAdminOrAbove },
  ];

  // ─── Search ───
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Debounce — har keystroke pe filter chalane se sidebar laggy hota hai (60+ items).
  // 120ms debounce smooth typing + instant feel deta hai.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 120);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searchActive = search.trim().length > 0;
  const matches = (label) => label.toLowerCase().includes(search.trim().toLowerCase());
  const highlight = (label) => {
    if (!searchActive) return label;
    const q = search.trim().toLowerCase();
    const idx = label.toLowerCase().indexOf(q);
    if (idx === -1) return label;
    return (
      <>
        {label.slice(0, idx)}
        <span className="bg-yellow-200 text-gray-900 rounded px-0.5">{label.slice(idx, idx + q.length)}</span>
        {label.slice(idx + q.length)}
      </>
    );
  };

  // ─── Preserve sidebar scroll on navigation ───
  // Sidebar nav scroll position ko persist karte hain — agar user niche se item
  // click kare to nav-bar wapas top pe scroll na ho.
  useEffect(() => {
    const nav = document.getElementById('sidebar-nav');
    if (!nav) return;
    const saved = sessionStorage.getItem('sidebarScrollTop');
    if (saved) nav.scrollTop = parseInt(saved, 10) || 0;
    const onScroll = () => {
      sessionStorage.setItem('sidebarScrollTop', String(nav.scrollTop));
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => nav.removeEventListener('scroll', onScroll);
  }, []);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-gray-200">
        <AxialupLogo size={36} withText />
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-200">
        {isDistributor && user?.agency_names?.length > 0 ? (
          <>
            <div className="text-sm font-semibold text-gray-900 truncate" title={user.agency_names.join(', ')} data-testid="sidebar-agency-name">
              {user.agency_names[0]}
              {user.agency_names.length > 1 && <span className="text-xs text-gray-500 font-normal"> +{user.agency_names.length - 1}</span>}
            </div>
            <div className="text-xs text-gray-500 truncate" title={user.name}>Owner: {user?.name}</div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </>
        )}
        <div className="mt-1">
          <span className={`text-xs px-2 py-0.5 ${isOwner ? 'bg-[#002FA7] text-white' : isAssistant ? 'bg-purple-600 text-white' : isDistributor ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
            {isOwner ? 'OWNER' : user?.role?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Distributor Balance + Fund Request */}
      {isDistributor && (
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={18} className="text-emerald-600" />
            <span className="text-xs text-gray-500">Available Balance</span>
          </div>
          <p className={`text-lg font-bold ${(distBalance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="sidebar-balance">
            {formatCurrency(distBalance)}
          </p>
          {distBalance !== null && distBalance < 5000 && (
            <p className="text-[10px] text-red-500 mt-0.5">Low balance!</p>
          )}
          <Button
            size="sm"
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-xs gap-1.5 h-8"
            onClick={() => { navigate('/distributor-dashboard'); setMobileOpen(false); }}
            data-testid="sidebar-fund-request-btn"
          >
            <PaperPlaneTilt size={14} /> Fund Request
          </Button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto scroll-smooth scrollbar-thin" id="sidebar-nav" style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 transparent' }}>
        {/* Search box */}
        <div className="px-2 pb-2 sticky top-0 bg-white z-10">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search menu..."
              className="w-full h-8 pl-8 pr-7 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
              data-testid="sidebar-search"
            />
            {searchActive && (
              <button
                onClick={() => { setSearchInput(''); setSearch(''); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-0.5"
                title="Clear"
                data-testid="sidebar-search-clear"
              ><X size={12} /></button>
            )}
          </div>
        </div>

        {/* Standalone top items */}
        {topItems.filter(item => item.show && (!searchActive || matches(item.label))).map((item) => (
          <NavLink
            key={item.to + item.label}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon size={20} />
            <span>{highlight(item.label)}</span>
          </NavLink>
        ))}

        {/* Vendor flat menu — only when isVendor */}
        {isVendor && vendorItems.filter(item => item.show && (!searchActive || matches(item.label))).map((item) => (
          <NavLink
            key={item.to + item.label}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon size={20} />
            <span>{highlight(item.label)}</span>
          </NavLink>
        ))}

        {/* Grouped sections */}
        {!isVendor && sections.map((section) => {
          const visibleItems = section.items.filter(it => it.show && (!searchActive || matches(it.label)));
          if (visibleItems.length === 0) return null;
          // When searching, auto-expand sections with matches; else respect user toggle.
          const isOpen = searchActive ? true : !!openSections[section.key];
          const SectionIcon = section.icon;
          return (
            <div key={section.key} className="mt-2 pt-2 border-t border-gray-100" data-testid={`section-${section.key}`}>
              <button
                type="button"
                onClick={() => !searchActive && toggleSection(section.key)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                data-testid={`section-${section.key}-toggle`}
              >
                <span className="flex items-center gap-2">
                  <SectionIcon size={16} className="text-[#002FA7]" />
                  {section.label}
                  <span className="text-[10px] font-normal text-gray-400 normal-case tracking-normal">({visibleItems.length})</span>
                </span>
                {!searchActive && (isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
              </button>
              {isOpen && (
                <div className="mt-1 space-y-0.5">
                  {visibleItems.map(item => (
                    <NavLink
                      key={item.to + item.label}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `sidebar-link pl-6 ${isActive ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon size={18} />
                      <span>{highlight(item.label)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom standalone items */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          {bottomItems.filter(item => item.show && (!searchActive || matches(item.label))).map((item) => (
            <NavLink
              key={item.to + item.label}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon size={20} />
              <span>{highlight(item.label)}</span>
            </NavLink>
          ))}
        </div>

        {/* No-results hint */}
        {searchActive && (() => {
          const total =
            topItems.filter(i => i.show && matches(i.label)).length +
            (isVendor ? vendorItems.filter(i => i.show && matches(i.label)).length : 0) +
            (!isVendor ? sections.reduce((acc, s) => acc + s.items.filter(it => it.show && matches(it.label)).length, 0) : 0) +
            bottomItems.filter(i => i.show && matches(i.label)).length;
          if (total === 0) {
            return <div className="text-center text-xs text-gray-400 py-4 px-2" data-testid="sidebar-search-empty">"{search}" se koi menu match nahi hua</div>;
          }
          return null;
        })()}
      </nav>

      {/* Scroll Buttons */}
      <div className="flex border-t border-gray-200">
        <button onClick={() => document.getElementById('sidebar-nav')?.scrollBy({ top: -120, behavior: 'smooth' })} className="flex-1 py-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="scroll-up-btn">
          <CaretUp size={16} weight="bold" />
        </button>
        <div className="w-px bg-gray-200" />
        <button onClick={() => document.getElementById('sidebar-nav')?.scrollBy({ top: 120, behavior: 'smooth' })} className="flex-1 py-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="scroll-down-btn">
          <CaretDown size={16} weight="bold" />
        </button>
      </div>

      {/* Download Role Guide PDF — visible to Admin/Owner/Assistant/CA */}
      {(isAdminOrAbove || user?.role === 'accountant') && (
        <div className="px-4 pb-1">
          <a
            href={`${API_BASE}/api/docs/role-guide`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-blue-700 hover:text-blue-900 hover:underline py-1"
            data-testid="download-role-guide"
          >
            <FileText size={14} />
            <span>Role Guide PDF</span>
          </a>
        </div>
      )}

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start gap-3 text-gray-600 hover:text-red-600 hover:bg-red-50"
          data-testid="logout-btn"
        >
          <SignOut size={20} />
          <span>Logout</span>
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Top Bar — full width opaque bar prevents content overlap with hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 h-12 bg-white border-b border-gray-200 flex items-center px-3 shadow-sm">
        <button
          className="p-1.5 hover:bg-gray-100 rounded-md"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <List size={22} />}
        </button>
        <div className="flex items-center gap-2 ml-3">
          <AxialupLogo size={28} withText />
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar z-40 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        {SidebarContent()}
      </aside>
    </>
  );
}
